import {
  Bot,
  ScrollText,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { Agent } from "@/types/agent-types";
import type { Skill } from "@/services/skills-service";

/**
 * Supported slash menu modes for the composer palette.
 * Each mode decides which result set to render inside the floating menu.
 */
export type SlashMenuMode = "root" | "agents" | "comments" | "skills";

/**
 * Slash command option shown in the root command palette.
 * Commands can open nested menus or stay pinned as context chips.
 */
export interface SlashCommandOption {
  /**
   * Stable identifier for the command option.
   * Used for selection and keyboard navigation state.
   */
  id: string;

  /**
   * Visible command label without the leading slash.
   * Kept concise so results scan quickly in the palette.
   */
  label: string;

  /**
   * Human-friendly name shown in the slash menu and chip.
   * This matches the Codex-style row title rather than the raw slash token.
   */
  title: string;

  /**
   * Supporting copy explaining the command outcome.
   * Helps users understand the command before they select it.
   */
  description: string;

  /**
   * Icon used in the command row.
   * Mirrors the compact visual treatment used across the app.
   */
  icon: LucideIcon;

  /**
   * Optional nested mode opened by the command.
   * Commands like /agent and /comment use this to drill deeper.
   */
  nextMode?: Exclude<SlashMenuMode, "root">;
}

/**
 * Slash agent option used by the nested agent picker.
 * Real agents can carry the original Agent object while dummy rows stay UI-only.
 */
export interface SlashAgentOption {
  /**
   * Stable identifier for the option.
   * For real agents this matches the agent id from the API.
   */
  id: string;

  /**
   * Display name shown in the slash picker.
   * This is also reused in the preview chip for dummy selections.
   */
  name: string;

  /**
   * Short role summary for scanning.
   * Explains what kind of work this specialist handles.
   */
  role: string;

  /**
   * Optional live agent backing the option.
   * When present the slash selection can update real chat routing.
   */
  agent?: Agent;
}

/**
 * Slash skill option used by the dollar-triggered skill palette.
 * Surfaces reusable instruction bundles the user can attach to the prompt.
 */
export interface SlashSkillOption {
  /**
   * Stable identifier for the option — matches the Skill row id from the API.
   */
  id: string;

  /**
   * Display name shown in the slash picker.
   */
  name: string;

  /**
   * Short description for scanning. Falls back to a generic line when the
   * underlying skill record has no description.
   */
  description: string;

  /**
   * Stable slug used as the token's serialized value so the runtime can
   * resolve the skill independently of its display name.
   */
  slug: string;

  /**
   * Where the skill comes from. `platform` skills are curated by Taqat;
   * `workspace` skills are authored by the user's team.
   */
  scope: "platform" | "workspace";

  /**
   * Original skill record. Kept around so callers can read instructions or
   * other metadata without a second fetch.
   */
  skill?: Skill;
}

/**
 * Root slash commands shown when the user types `/`.
 * A mix of nested actions and starter prompt scaffolds is enough for the first pass.
 */
export const SLASH_COMMAND_OPTIONS: SlashCommandOption[] = [
  {
    id: "compact",
    label: "compact",
    title: "Compact",
    description: "Summarize older turns to free up context",
    icon: ScrollText,
  },
  {
    id: "agent",
    label: "agent",
    title: "Agent",
    description: "Choose which specialist should handle this request",
    icon: Bot,
    nextMode: "agents",
  },
];

/**
 * Fallback specialist rows for the agent submenu.
 * These dummy options keep the interaction testable even before live data is available.
 */

/**
 * Suggested note presets for the comment submenu.
 * These give users a fast starting point while still allowing custom text.
 */
export const SUGGESTED_SLASH_COMMENTS = [
  "Be concise",
  "Ask clarifying questions only if truly needed",
  "Write this for leadership",
  "Focus on edge cases",
  "Use a friendly tone",
];

/**
 * Converts live agents into slash agent options.
 * The original agent object is preserved so the composer can update real routing.
 */
export function mapAgentsToSlashOptions(agents: Agent[]): SlashAgentOption[] {
  return agents.map((agent) => ({
    id: agent.id,
    name: agent.name,
    role: agent.description || "Specialist agent",
    agent,
  }));
}

/**
 * Converts skill records into slash skill options. The original skill is
 * preserved so the composer can read instructions or other metadata without
 * a second fetch.
 */
export function mapSkillsToSlashOptions(skills: Skill[]): SlashSkillOption[] {
  return skills.map((skill) => ({
    id: skill.id,
    name: skill.name,
    description: skill.description || "Reusable instruction bundle",
    slug: skill.slug,
    scope: skill.scope,
    skill,
  }));
}

/**
 * Parses the textarea value to decide whether the slash palette should open.
 * The first pass only activates when the message starts with a slash command.
 */
